/**
 * **No signal in the main app: into the active rave** (the user, 2026-10-30: "only the rave app needs offline, the
 * rest is not relevant — the main app in offline mode will not work, it only lets you into the active rave app").
 * Only a rave keeps the app for offline (`offline/register.ts` `keepsWorkerHere`), so on `app.` with no connection —
 * at the start, or when it goes while the page is open — the page goes to the rave this account is at: the one on
 * NOW, else the next one coming, else the one that ended last (`activeRave`), from the copy of My raves this device
 * keeps (`my-raves-<account>`, `lib/api.ts`). Signed in only; nothing kept, nothing to do.
 */
import { useEffect } from 'react';
import { currentAccountId, storedMyFestivals, type MyFestival } from '@/lib/api';
import { overForOffline } from '@/offline/pastRaveRule';
import { apiUrl, festivalUrl, isAppHost } from '@/lib/tenant';
import { probeConnection } from '@/lib/reach';

/** The rave somebody is at: on now, else the next to start, else the last to end. Never one they only run. */
export function activeRave(festivals: readonly MyFestival[], now = Date.now()): MyFestival | null {
  // A rave over by more than two days keeps nothing offline (`pastRaveRule.ts`, 2026-11-02): never one to go to.
  const mine = festivals.filter((f) => f.attending !== false && f.gatesOpenAt && f.endsAt && !overForOffline(f.endsAt, now));
  const at = (s: string | null) => Date.parse(s!);
  const live = mine.filter((f) => at(f.gatesOpenAt) <= now && now <= at(f.endsAt));
  if (live.length) return live.sort((a, b) => at(a.endsAt) - at(b.endsAt))[0]!;
  const next = mine.filter((f) => at(f.gatesOpenAt) > now).sort((a, b) => at(a.gatesOpenAt) - at(b.gatesOpenAt));
  if (next.length) return next[0]!;
  return mine.sort((a, b) => at(b.endsAt) - at(a.endsAt))[0] ?? null;
}

/**
 * My raves and Upcoming raves stay with no signal, listing only the raves kept on this phone to enter (the QA walk,
 * 2026-11-02: "when offline on My raves or Upcoming raves it should show only the offline raves that are in the phone
 * and let the user enter them") — `Dashboard`, `Directory`. From them, only a rave on NOW is gone to by itself.
 */
export const OFFLINE_LIST_PAGES = ['/', '/my-raves'];

/** Where the main app goes with no signal, or null to stay. `listPage`: only to a rave on now. Exported for the test. */
export function offlineRaveUrl(now = Date.now(), listPage = false): string | null {
  const who = currentAccountId();
  if (!who) return null;
  const rave = activeRave(storedMyFestivals()?.festivals ?? [], now);
  if (!rave) return null;
  const live = Date.parse(rave.gatesOpenAt!) <= now && now <= Date.parse(rave.endsAt!);
  if (listPage && !live) return null;
  return festivalUrl(rave.subdomain, rave.editionSlug);
}

export function OfflineToRave() {
  useEffect(() => {
    if (!isAppHost()) return undefined;
    let gone = false;
    const check = async () => {
      if (gone || (await probeConnection(apiUrl())) !== 'offline') return;
      const url = offlineRaveUrl(Date.now(), OFFLINE_LIST_PAGES.includes(window.location.pathname.replace(/\/+$/, '') || '/'));
      if (url && !gone) { gone = true; window.location.assign(url); }
    };
    void check();
    const offline = () => { void check(); };
    window.addEventListener('offline', offline);
    return () => { window.removeEventListener('offline', offline); };
  }, []);
  return null;
}
